<template>
  <div>
    <Breadcrumbs
      :paths="['/model', $route.path]"
    ></Breadcrumbs>
    <v-toolbar>
      <v-toolbar-title>
        {{ $t("message.model.model_detail") }}&nbsp;({{ $route.params.mid }})
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="ma-2" @click="get_detail_data(get_prefix() + 'model/', [$route.params.mid])">
            mdi-refresh
          </v-icon>
        </template>
        <span>{{ $t("message.common.refresh") }}</span>
      </v-tooltip>
    </v-toolbar>
    <v-card class="pa-5 mt-1" v-if="fetch_status === 'getting'">
      {{ $t("message.common.getting", { "id": $route.params.mid }) }}
      <p class="text-right">
        <v-btn :to="get_prev(get_prefix() + 'module')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <v-card class="pa-5 mt-1" v-else-if="data">
      <v-tabs
        background-color="primary"
        dark
        v-model="tab"
        class="pb-3"
      >
        <v-tab href="#information">
          {{ $t("message.common.information") }}
        </v-tab>
        <v-tab href="#usage">
          {{ $t("message.common.usage") }}
        </v-tab>

        <v-tabs-items v-model="tab">
          <!-- tab[0]: information -->
          <v-tab-item value="information">
            <v-card>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.mid") }}
                      </td>
                      <td>
                        {{ data.mid }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.title") }}
                      </td>
                      <td class="table-name">
                        <template v-if="changeable()">
                          <v-card
                            class="d-flex justify-space-between"
                            flat
                          >
                            <v-card flat class="align-self-center flex-grow-1">
                              <v-text-field
                                v-model="data.name"
                                :rules="[
                                  v => (v && v.length <= 80) || $t('message.common.over_text', { length: 80 })
                                ]"
                                counter="80"
                              >
                              </v-text-field>
                            </v-card>
                            <template v-if="data.name !== original_data.name">
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-icon v-on="on" class="ma-2" @click="data.name = original_data.name">
                                    mdi-undo-variant
                                  </v-icon>
                                </template>
                                <span>{{ $t("message.common.undo") }}</span>
                              </v-tooltip>
                            </template>
                          </v-card>
                        </template>
                        <template v-else>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <span v-on="on">
                                {{ data.name }}
                              </span>
                            </template>
                            <span>{{ data.name }}</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.base_model") }}
                      </td>
                      <td>
                        <FieldName
                          :id="data.field"
                          :source_lang="data.source_lang"
                          :target_lang="data.target_lang"
                          type="trainable"
                        ></FieldName>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.direction") }}
                      </td>
                      <td>
                        {{ $t("message.common.lang_" + data.source_lang) }}
                        &rarr;
                        {{ $t("message.common.lang_" + data.target_lang) }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.size") }}(MB)
                      </td>
                      <td>
                        {{ (data.size / 1024 / 1024).toFixed(1) | comma_digit }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $t("message.common.engine") }}
                      </td>
                      <td>
                        {{ $t("message.common.engine_" + data.engine) }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $t("message.common.group") }}
                      </td>
                      <td>
                        <IdName
                          :mode="mode"
                          type="group"
                          :ids="[data.gid]"
                          :nav_path="nav_path"
                        ></IdName>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $t("message.common.user") }}
                      </td>
                      <td>
                        <IdName
                          :mode="mode"
                          type="user"
                          :ids="[data.uid]"
                          :nav_path="nav_path"
                        ></IdName>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.permission") }}
                      </td>
                      <td class="pt-0 pb-0">
                        <template v-if="changeable()">
                          <v-card
                            class="d-flex justify-space-between"
                            flat
                          >
                            <v-card flat class="align-self-center flex-grow-1">
                              <v-select
                                :items="permissions"
                                item-text="label"
                                item-value="value"
                                v-model="data.permission"
                                single-line
                                hide-details
                                dense
                              >
                                <template v-slot:item="props">
                                  {{ $t('message.common.' + props.item.label) }}
                                </template>
                                <template v-slot:selection="props">
                                  {{ $t('message.common.' + props.item.label) }}
                                </template>
                              </v-select>
                            </v-card>
                            <template v-if="data.permission !== original_data.permission">
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-icon v-on="on" class="ma-2" @click="data.permission = original_data.permission">
                                    mdi-undo-variant
                                  </v-icon>
                                </template>
                                <span>{{ $t("message.common.undo") }}</span>
                              </v-tooltip>
                            </template>
                          </v-card>
                        </template>
                        <template v-else>
                          {{ $t('message.common.' + data.permission) }}
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.corpus") }}
                      </td>
                      <td class="pt-0 pb-0">
                        <v-list v-for="cid in data.cid" :key="cid">
                          <v-list-item dense class="pa-0">
                            <IdName
                              :mode="mode"
                              type="corpus"
                              :ids="[cid]"
                              :nav_path="nav_path"
                            ></IdName>
                          </v-list-item>
                        </v-list>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.model.job") }}
                      </td>
                      <td class="pt-0 pb-0">
                        <v-list>
                          <v-list-item dense class="pa-0">
                            <IdName
                              :mode="mode"
                              type="job"
                              :ids="[data.jid]"
                              :nav_path="nav_path"
                            ></IdName>
                          </v-list-item>
                        </v-list>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.ctime") }}
                      </td>
                      <td>
                        {{ $d(1000 * data.ctime, 'long', $store.getters.langs[$i18n.locale].locale) }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.etime") }}
                      </td>
                      <td>
                        <template v-if="data.etime !== -1">
                          {{ $d(1000 * data.etime, 'long', $store.getters.langs[$i18n.locale].locale) }}
                        </template>
                        <template v-else>
                          -
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        {{ $i18n.t("message.common.operation") }}
                      </td>
                      <td>
                        <div class="buttonContents">
                          <template v-if="mode === 'user'">
                            <v-btn
                              color="primary"
                              @click="$router.push('/translate?command_type=translate&model_type=model&model=' + data.mid + '&source_lang=' + data.source_lang + '&target_lang=' + data.target_lang)"
                            >
                              {{ $t("message.model.translate_with_this") }}
                            </v-btn>
                          </template>
                          <v-btn
                            color="primary"
                            @click="$refs.parallel_viewer && $refs.parallel_viewer.open(data.name, 'model', data.mid)"
                          >
                            {{ $t("message.viewer.preview") }}
                          </v-btn>
                          <template v-if="deletable()">
                            <v-btn
                              @click="confirm_delete_data(get_prefix() + 'model', [data.mid])"
                            >
                              {{ $t("message.common.delete") }}
                            </v-btn>
                          </template>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-tab-item>

          <!-- tab[1]: usage -->
          <v-tab-item value="usage">
            <v-card>
              <Usage
                ref="usage"
                :path="'/' + get_prefix() + 'model/' + $route.params.mid + '/stat'"
              ></Usage>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
      <p class="text-right">
        <template v-if="tab === 'information' && changeable()">
          <v-btn
            color="primary"
            :disabled="!modifiable"
            @click="change_data('model', [data.mid])"
          >
            {{ $t("message.common.modify") }}
          </v-btn>
        </template>
        <v-btn :to="get_prev(get_prefix() + 'model')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <v-card class="pa-5 mt-1" v-else>
      {{ $t("message.common.no_id", { "id": $route.params.mid }) }}
      <p class="text-right">
        <v-btn :to="get_prev(get_prefix() + 'model')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <ParallelViewer
      ref="parallel_viewer"
      :mode="mode"
    ></ParallelViewer>
    <Confirm
      ref="delete_confirm"
      :message="$t('message.common.delete_confirm')"
      :positive="$t('message.common.yes')"
      :negative="$t('message.common.no')"
    >
    </Confirm>
  </div>
</template>

<script>
import Breadcrumbs from '../components/Breadcrumbs';
import IdName from '../components/IdName';
import FieldName from '../components/FieldName';
import Usage from '../components/Usage';
import Confirm from '../components/Confirm';
import ParallelViewer from '../components/ParallelViewer';
import { res_detail } from "../js/res_detail";
import { http_error } from "../js/http_error";

export default {
  name: 'model_detail',
  components: {
    Breadcrumbs,
    IdName,
    FieldName,
    Usage,
    Confirm,
    ParallelViewer
  },
  mixins: [
    res_detail,
    http_error
  ],
  props: {
    mode: String
  },
  data: function(){
    return {
      module_id: "28",
      nav_path: null,
      fetch_status: "getting",
      data: null,
      original_data: null,
      editable: {
        permission: () => true,
        name: () => (this.data && this.data.name.length > 0 && this.data.name.length <= 80)
      },
      tab: "information",
      permissions: [
        {
          "label": "user",
          "value": "user"
        },
        {
          "label": "group",
          "value": "group"
        },
        {
          "label": "organization",
          "value": "organization"
        }
      ]
    }
  },
  mounted: function(){
    this.get_detail_data(this.get_prefix() + 'model/', [this.$route.params.mid]);
  },
  methods: {
  }
}
</script>

<style scoped>
.v-data-table
  tbody
  tr:hover:not(.v-data-table__expanded__content) {
  background: inherit !important;
}
.v-data-table td:first-of-type {
  white-space : nowrap;
}
.buttonContents {
  margin-bottom: 8px;
}
.buttonContents > button {
  margin-top: 8px;
  margin-right: 8px;
}
.v-data-table {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 12px;
}
.v-data-table
  tbody > tr > td:first-of-type {
  background-color: #eeeeee;
}
.table-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
}
</style>
